import { useState, useMemo, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { useKeywords, useReplaceKeywords, useTriggerSync } from '../hooks/useSDI'
import { MarketTabsLayout } from '../components/sdi/shared/MarketTabsLayout'
import type { KeywordItem } from '../hooks/sdi.types'
import {
  Search, Upload, RefreshCw, Check, X, ArrowUpDown,
  AlertCircle, ChevronDown, Trash2, History, Download,
} from 'lucide-react'
import { cn } from '@/shared/lib/utils'

const fmtCompact = new Intl.NumberFormat('fr-FR', { notation: 'compact', maximumFractionDigits: 1 })
const fmtEur = (v: number) => `${v.toFixed(2)} €`

const tagOptions = ['keep', 'expand', 'test', 'exclude'] as const
type TagType = typeof tagOptions[number] | 'orphan'
const tagColors: Record<TagType | string, { bg: string; text: string }> = {
  keep: { bg: 'bg-emerald-100', text: 'text-emerald-700' },
  expand: { bg: 'bg-blue-100', text: 'text-blue-700' },
  test: { bg: 'bg-amber-100', text: 'text-amber-700' },
  exclude: { bg: 'bg-red-100', text: 'text-red-700' },
  orphan: { bg: 'bg-paper-warm', text: 'text-ink-faint' },
}

type SortKey = 'keyword' | 'avg_searches' | 'cpc_high' | 'opportunity_score'

// ── Tag Chip (one-click cycle) ───────────────────────────────

function TagChip({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [showMenu, setShowMenu] = useState(false)
  const cfg = tagColors[value] ?? tagColors.keep

  return (
    <div className="relative">
      <button onClick={() => setShowMenu(!showMenu)}
        className={cn('inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold transition-all hover:ring-2 hover:ring-gold', cfg.bg, cfg.text)}>
        {value} <ChevronDown size={10} />
      </button>
      {showMenu && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setShowMenu(false)} />
          <div className="absolute z-30 top-full mt-1 left-0 bg-white border border-ink-border rounded-card shadow-elevated py-1 min-w-[110px] animate-fade-in">
            {tagOptions.map((opt) => {
              const c = tagColors[opt]
              return (
                <button key={opt} onClick={() => { onChange(opt); setShowMenu(false) }}
                  className={cn('w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-paper', value === opt && 'font-bold')}>
                  <span className={cn('w-2.5 h-2.5 rounded-full', c.bg)} />
                  <span className={c.text}>{opt}</span>
                </button>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}

// ── Filter Chips ─────────────────────────────────────────────

function FilterChips({ keywords, activeFilter, onFilter }: {
  keywords: KeywordItem[]; activeFilter: string; onFilter: (tag: string) => void
}) {
  const counts = useMemo(() => {
    const c: Record<string, number> = { all: keywords.length }
    keywords.forEach((kw) => { c[kw.status] = (c[kw.status] || 0) + 1 })
    return c
  }, [keywords])

  const chips = [
    { key: 'all', label: 'Tous' },
    ...tagOptions.map((t) => ({ key: t, label: t.charAt(0).toUpperCase() + t.slice(1) })),
    { key: 'orphan', label: 'Orphelin' },
  ]

  return (
    <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
      {chips.map((chip) => {
        const count = counts[chip.key] ?? 0
        if (chip.key !== 'all' && count === 0) return null
        const cfg = chip.key === 'all' ? null : tagColors[chip.key]
        return (
          <button key={chip.key} onClick={() => onFilter(chip.key === 'all' ? '' : chip.key)}
            className={cn('flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap',
              (activeFilter === chip.key || (activeFilter === '' && chip.key === 'all'))
                ? 'bg-brick text-white'
                : cfg ? cn(cfg.bg, cfg.text) : 'bg-paper-warm text-ink hover:bg-paper-warm')}>
            {chip.label}
            <span className={cn('text-[10px] font-bold', activeFilter === chip.key || (activeFilter === '' && chip.key === 'all') ? 'text-white/80' : 'opacity-60')}>{count}</span>
          </button>
        )
      })}
    </div>
  )
}

// ── Import CSV Modal ─────────────────────────────────────────

function ImportCsvModal({ open, onClose, onImport }: { open: boolean; onClose: () => void; onImport: (kws: string[]) => void }) {
  const [text, setText] = useState('')
  if (!open) return null
  const handleImport = () => { onImport(text.split('\n').map((l) => l.split(',')[0]?.trim()).filter(Boolean)); setText(''); onClose() }
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="bg-white rounded-2xl border border-ink-border shadow-elevated w-full max-w-md p-6 animate-slide-up">
        <h3 className="text-lg font-semibold text-ink mb-4">Importer des mots-clés</h3>
        <p className="text-sm text-ink-muted mb-3">Un mot-clé par ligne ou CSV (1ère colonne = keyword).</p>
        <textarea value={text} onChange={(e) => setText(e.target.value)}
          className="w-full h-40 border border-ink-border rounded-card p-3 text-sm font-mono resize-none focus:outline-none focus:ring-2 focus:ring-gold"
          placeholder={"plombier lorient\nplombier urgence lorient"} />
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-4 py-2 text-sm text-ink hover:bg-paper-warm rounded-lg">Annuler</button>
          <button onClick={handleImport} className="px-4 py-2 text-sm font-semibold text-white bg-brick hover:bg-brick-hover rounded-lg">Importer</button>
        </div>
      </div>
    </div>
  )
}

// ── Page ─────────────────────────────────────────────────────

export function KeywordClusterPage() {
  const { id: marketId } = useParams<{ id: string }>()
  const { data: keywords, isLoading } = useKeywords(marketId!)
  const replaceKeywords = useReplaceKeywords()
  const triggerSync = useTriggerSync()

  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [localEdits, setLocalEdits] = useState<Record<string, string>>({})
  const [sortKey, setSortKey] = useState<SortKey>('opportunity_score')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const [filterText, setFilterText] = useState('')
  const [tagFilter, setTagFilter] = useState('')
  const [showImport, setShowImport] = useState(false)
  const [hasEdits, setHasEdits] = useState(false)

  const allKeywords = (keywords ?? []) as KeywordItem[]

  const sorted = useMemo(() => {
    let list = [...allKeywords]
    if (tagFilter) {
      if (tagFilter === 'orphan') list = list.filter((k) => k.avg_searches === null)
      else list = list.filter((k) => (localEdits[k.id] ?? k.status) === tagFilter)
    }
    if (filterText) { const q = filterText.toLowerCase(); list = list.filter((k) => k.keyword.toLowerCase().includes(q)) }
    list.sort((a, b) => {
      const va = a[sortKey] ?? 0; const vb = b[sortKey] ?? 0
      if (typeof va === 'string') return sortDir === 'asc' ? va.localeCompare(vb as string) : (vb as string).localeCompare(va)
      return sortDir === 'asc' ? (va as number) - (vb as number) : (vb as number) - (va as number)
    })
    return list
  }, [allKeywords, sortKey, sortDir, filterText, tagFilter, localEdits])

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => d === 'asc' ? 'desc' : 'asc')
    else { setSortKey(key); setSortDir('desc') }
  }

  const handleTagChange = useCallback((kwId: string, newTag: string) => {
    setLocalEdits((p) => ({ ...p, [kwId]: newTag })); setHasEdits(true)
  }, [])

  const handleBulkTag = (tag: string) => { const e = { ...localEdits }; selected.forEach((id) => { e[id] = tag }); setLocalEdits(e); setSelected(new Set()); setHasEdits(true) }
  const handleBulkDelete = () => { const e = { ...localEdits }; selected.forEach((id) => { e[id] = 'exclude' }); setLocalEdits(e); setSelected(new Set()); setHasEdits(true) }

  const handleSave = () => {
    const payload = allKeywords.map((kw) => ({
      keyword: kw.keyword, status: localEdits[kw.id] ?? kw.status,
      tag: kw.tag ?? undefined, cluster_group: kw.cluster_group ?? undefined,
    }))
    replaceKeywords.mutate({ marketId: marketId!, keywords: payload })
    setLocalEdits({}); setHasEdits(false)
  }

  const handleImport = (newKws: string[]) => {
    const existing = allKeywords.map((kw) => ({ keyword: kw.keyword, status: localEdits[kw.id] ?? kw.status, tag: kw.tag ?? undefined, cluster_group: kw.cluster_group ?? undefined }))
    const existSet = new Set(existing.map((e) => e.keyword.toLowerCase()))
    const additions = newKws.filter((k) => !existSet.has(k.toLowerCase())).map((k) => ({ keyword: k, status: 'keep' as const }))
    replaceKeywords.mutate({ marketId: marketId!, keywords: [...existing, ...additions] })
  }

  const exportCsv = () => {
    const header = ['keyword', 'demand', 'cpc_high', 'tag', 'decision', 'cluster']
    const rows = sorted.map((kw) => [kw.keyword, kw.avg_searches ?? '', kw.cpc_high ?? '', kw.tag ?? '', localEdits[kw.id] ?? kw.status, kw.cluster_group ?? ''])
    const csv = [header, ...rows].map((r) => r.join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `sdi_cluster_${marketId}.csv`; a.click()
  }

  const maxVersion = allKeywords.length ? Math.max(...allKeywords.map((k) => k.version_id), 0) : 0

  return (
    <MarketTabsLayout>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div>
              <h2 className="text-lg font-bold text-ink">Cluster de mots-clés</h2>
              <p className="text-sm text-ink-faint">{allKeywords.length} mot(s)-clé(s)</p>
            </div>
            <button className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-paper-warm text-xs font-medium text-ink-muted hover:bg-paper-warm" title="Historique des versions">
              <History size={12} /> Cluster v{maxVersion}
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={exportCsv} className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-ink bg-paper-warm hover:bg-paper-warm rounded-lg"><Download size={14} /> Export</button>
            <button onClick={() => setShowImport(true)} className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-ink bg-paper-warm hover:bg-paper-warm rounded-lg"><Upload size={14} /> Importer</button>
            {hasEdits && (
              <>
                <button onClick={handleSave} className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-white bg-brick hover:bg-brick-hover rounded-lg"><Check size={14} /> Sauvegarder</button>
                <button onClick={() => triggerSync.mutate(marketId!)} className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-brick bg-gold-light hover:bg-gold-light rounded-lg"><RefreshCw size={14} /> Re-sync</button>
              </>
            )}
          </div>
        </div>

        {/* Filter Chips */}
        <FilterChips keywords={allKeywords} activeFilter={tagFilter} onFilter={setTagFilter} />

        {/* Search */}
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint" />
          <input type="text" placeholder="Filtrer les mots-clés…" value={filterText} onChange={(e) => setFilterText(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 text-sm border border-ink-border rounded-card focus:outline-none focus:ring-2 focus:ring-gold" />
        </div>

        {/* Bulk Actions */}
        {selected.size > 0 && (
          <div className="flex items-center gap-3 bg-gold-light border border-brick rounded-card px-4 py-2.5 animate-slide-up">
            <span className="text-sm font-medium text-brick-dark">{selected.size} sélectionné(s)</span>
            <div className="flex items-center gap-2 ml-auto">
              {tagOptions.map((tag) => {
                const c = tagColors[tag]
                return <button key={tag} onClick={() => handleBulkTag(tag)} className={cn('px-2.5 py-1 rounded-lg text-xs font-medium', c.bg, c.text)}>{tag}</button>
              })}
              <button onClick={handleBulkDelete} className="p-1.5 rounded-lg text-red-600 hover:bg-red-50"><Trash2 size={14} /></button>
              <button onClick={() => setSelected(new Set())} className="p-1.5 rounded-lg text-ink-faint hover:bg-paper-warm"><X size={14} /></button>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-card border border-ink-border overflow-hidden overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-ink-border bg-paper">
                <th className="px-3 py-3 w-8"><input type="checkbox" className="rounded" onChange={(e) => e.target.checked ? setSelected(new Set(sorted.map((k) => k.id))) : setSelected(new Set())} /></th>
                {([['keyword', 'Mot-clé', 'text-left'], ['avg_searches', 'Demande', 'text-right'], ['cpc_high', 'CPC haut', 'text-right'], ['opportunity_score', 'Score opp.', 'text-right']] as const).map(([key, label, align]) => (
                  <th key={key} className={cn('px-4 py-3 font-medium text-ink-muted cursor-pointer hover:text-ink', align)} onClick={() => toggleSort(key)}>
                    <span className="inline-flex items-center gap-1">{label} <ArrowUpDown size={12} className={sortKey === key ? 'text-brick' : 'text-ink-faint'} /></span>
                  </th>
                ))}
                <th className="px-4 py-3 text-left font-medium text-ink-muted">Cluster</th>
                <th className="px-4 py-3 text-center font-medium text-ink-muted">Décision</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-border/30">
              {isLoading ? Array.from({ length: 8 }).map((_, i) => (
                <tr key={i} className="animate-pulse">{Array.from({ length: 7 }).map((_, j) => <td key={j} className="px-4 py-3"><div className="h-4 bg-paper-warm rounded w-3/4" /></td>)}</tr>
              )) : sorted.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-ink-faint">Aucun mot-clé trouvé</td></tr>
              ) : sorted.map((kw) => {
                const isOrphan = kw.avg_searches === null
                const effectiveTag = localEdits[kw.id] ?? kw.status
                return (
                  <tr key={kw.id} className={cn('hover:bg-paper transition-colors', selected.has(kw.id) && 'bg-gold-light/30')}>
                    <td className="px-3 py-3"><input type="checkbox" checked={selected.has(kw.id)} onChange={() => { const n = new Set(selected); n.has(kw.id) ? n.delete(kw.id) : n.add(kw.id); setSelected(n) }} className="rounded" /></td>
                    <td className="px-4 py-3">
                      <span className={cn('font-medium', isOrphan ? 'text-ink-faint italic' : 'text-ink')}>{kw.keyword}</span>
                      {isOrphan && <span className="ml-2 inline-flex items-center gap-0.5 text-[10px] text-ink-faint"><AlertCircle size={10} /> orphelin</span>}
                    </td>
                    <td className="px-4 py-3 text-right text-ink">{kw.avg_searches != null ? fmtCompact.format(kw.avg_searches) : '—'}</td>
                    <td className="px-4 py-3 text-right text-ink">{kw.cpc_high != null ? fmtEur(kw.cpc_high) : '—'}</td>
                    <td className="px-4 py-3 text-right">{kw.opportunity_score != null ? <span className="font-semibold text-ink">{kw.opportunity_score.toFixed(1)}</span> : '—'}</td>
                    <td className="px-4 py-3 text-xs text-ink-muted">{kw.cluster_group ?? '—'}</td>
                    <td className="px-4 py-3 text-center"><TagChip value={effectiveTag} onChange={(v) => handleTagChange(kw.id, v)} /></td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        <ImportCsvModal open={showImport} onClose={() => setShowImport(false)} onImport={handleImport} />
      </div>
    </MarketTabsLayout>
  )
}
